import { useState, useEffect } from 'react';
import { getDeviceType, getOrientation, BREAKPOINTS } from '../utils/responsive';

/**
 * Custom hook for responsive design
 * Provides device type, orientation, and viewport dimensions
 * Updates automatically on window resize and orientation change
 */
export const useResponsive = () => {
  const [deviceType, setDeviceType] = useState(() => getDeviceType());
  const [orientation, setOrientation] = useState(() => getOrientation());
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));

  useEffect(() => {
    const updateResponsive = () => {
      setDeviceType(getDeviceType());
      setOrientation(getOrientation());
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Initial update
    updateResponsive();

    // Debounce resize handler for better performance
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateResponsive, 150);
    };

    // Listen to resize and orientation change events
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      // Delay to ensure accurate dimensions after orientation change
      setTimeout(updateResponsive, 100);
    });

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', updateResponsive);
    };
  }, []);

  return {
    deviceType,
    orientation,
    viewport,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape',
    isMobileOrTablet: deviceType === 'mobile' || deviceType === 'tablet',
    breakpoints: BREAKPOINTS,
  };
};

export default useResponsive;

